<script lang="ts" setup>
import { Formatter, DateUtil } from "@/util";
import { Tooltip, LoadingSpinner } from "@/components";
import {
  useDocumentSpaceStatisticsQuery,
  Interval,
  Maybe,
} from "@/graphql/operations";
import { computed, nextTick, onBeforeUnmount, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import Visits from "./Visits.vue";

const router = useRouter();
const store = useStore();

const { fetching, data, error } = useDocumentSpaceStatisticsQuery({
  variables: reactive({
    includePreview: false,
  }),
  requestPolicy: "cache-and-network",
});

const visits = computed(() => data.value?.maLatestSpaceVisits?.nodes);

const totalCount = computed(() => data.value?.maLatestSpaceVisits?.totalCount);
const numSpacesWithoutTracking = computed(
  () => data.value?.documentSpaces?.totalCount,
);

const formatDuration = (duration: Interval) => {
  return Formatter.formatDuration({
    hours: duration.hours || 0,
    minutes: duration.minutes || 0,
    seconds: duration.seconds || 0,
  });
};

const selectedDocumentSpaceId = ref<string | undefined>(undefined);
const selectedControl = ref<HTMLButtonElement>();

const unselect = (restoreFocus = false) => {
  const control = selectedControl.value;
  selectedDocumentSpaceId.value = undefined;

  if (restoreFocus && control) {
    void nextTick(() => {
      control.focus();
      if (selectedControl.value === control) {
        selectedControl.value = undefined;
      }
    });
  } else {
    selectedControl.value = undefined;
  }
};

const select = (documentSpaceId: Maybe<string> | undefined, event: Event) => {
  if (!documentSpaceId) {
    return;
  }

  selectedDocumentSpaceId.value = documentSpaceId;
  selectedControl.value =
    event.currentTarget instanceof HTMLButtonElement
      ? event.currentTarget
      : event.currentTarget instanceof HTMLElement
        ? (event.currentTarget.querySelector<HTMLButtonElement>(
            ".space_row_button",
          ) ?? undefined)
        : undefined;
};

const canDisableSpaceTracking = computed(() =>
  store.getters.user.canDisableSpaceTracking(),
);
</script>

<template>
  <div class="pane page_content" data-test="classic-space-analytics">
    <div
      :class="{
        columns: selectedDocumentSpaceId !== undefined,
        title_container: true,
      }"
    >
      <div :class="{ col: selectedDocumentSpaceId !== undefined }">
        <div class="left">
          <h3>{{ $t("dashboard.title") }}</h3>

          <Tooltip
            keyboard-accessible
            v-if="
              canDisableSpaceTracking &&
              numSpacesWithoutTracking &&
              numSpacesWithoutTracking > 0
            "
          >
            <template v-slot>
              <span class="material-symbols-outlined"> help </span>
            </template>
            <template #tooltip>
              {{
                $t("dashboard.labelSpacesNotTracked", {
                  count: numSpacesWithoutTracking,
                })
              }}
            </template>
          </Tooltip>
        </div>
      </div>

      <div :class="{ col: selectedDocumentSpaceId !== undefined }">
        <template v-if="selectedDocumentSpaceId != undefined">
          <button
            type="button"
            class="close_button icon right"
            :aria-label="$t('dashboard.closeAnalyticsDetail')"
            @click="unselect(true)"
          >
            <span class="material-symbols-outlined"> close </span>
          </button>
        </template>
      </div>
    </div>

    <section>
      <div v-if="fetching" class="centered_container">
        <loading-spinner :color="'var(--classic-text, #444)'" />
      </div>

      <div v-else-if="error" class="message error" role="alert">
        {{ $t("error") }}
      </div>

      <div
        v-else-if="!fetching && visits && visits.length === 0"
        class="centered_container"
      >
        <span class="material-symbols-outlined big_text"> analytics </span>
        <h3>{{ $t("dashboard.noData.title") }}</h3>
        <span>
          {{ $t("dashboard.noData.textNoPreview") }}
        </span>
      </div>

      <template v-else>
        <div
          :class="{
            columns: selectedDocumentSpaceId !== undefined,
            fullheight: true,
          }"
        >
          <div :class="{ col: selectedDocumentSpaceId !== undefined }">
            <section class="data">
              <table class="data_table clickable">
                <tbody>
                  <tr
                    v-for="(visit, index) in visits"
                    :key="index"
                    @click="select(visit!.documentSpaceId, $event)"
                    :class="{
                      selected:
                        visit!.documentSpaceId === selectedDocumentSpaceId,
                    }"
                  >
                    <td>
                      <button
                        type="button"
                        class="space_row_button"
                        aria-controls="space-analytics-detail"
                        :aria-expanded="
                          visit!.documentSpaceId === selectedDocumentSpaceId
                        "
                        @click.stop="select(visit!.documentSpaceId, $event)"
                      >
                        {{ visit!.name }}
                      </button>
                    </td>
                    <td>
                      {{
                        Formatter.translateDate(visit!.latestVisitDay as string)
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>
          </div>

          <section
            id="space-analytics-detail"
            class="col detail"
            v-if="selectedDocumentSpaceId"
          >
            <visits :documentSpaceId="selectedDocumentSpaceId"></visits>
          </section>
        </div>
      </template>
    </section>
  </div>
</template>

<style scoped lang="scss">
* {
  font-size: 0.9rem;
}

.columns {
  display: flex;
  align-items: start;
  column-gap: 15px;
}

.columns.fullheight {
  height: calc(100vh - 155px);
}

.columns .col {
  width: 50%;
}

.columns.fullheight .col {
  height: 100%;
  overflow-y: scroll;
  box-sizing: border-box;
}

.columns .col:first-child {
  border-right: 1px solid var(--classic-border-subtle, #ebebeb);
}

h3 {
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 38px;
  margin-top: 0;
}

.section_title {
  padding: 1.5em 0 0.5em 14px;
  font-weight: 500;
}

table a:not(.link) {
  color: var(--classic-text, #444);
  font-weight: 500;
  text-decoration: none;
  display: inline-block;
  width: 100%;
}

table .link {
  font-weight: 400;
  color: var(--classic-text, #444);
}

.material-symbols-outlined {
  font-size: 1.4em;
}

table a:hover {
  text-decoration: underline;
}

.centered_container {
  text-align: center;
  margin-top: 5%;
}

.header {
  margin: 0 1em;
}

table.data_table {
  box-shadow: none;
}

table tr th,
table tr td {
  width: 155px;
}

table.data_table.clickable tr:hover {
  background-color: var(--classic-surface-hover, #f2f5fa);
}

table.data_table.clickable tr.selected,
table.data_table.clickable tr.selected:hover {
  background-color: var(--classic-selection, #e1ebf5);
}

.space_row_button {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;
  width: 100%;
}

table tr th:first-child,
table tr td:first-child {
  width: calc(100% - 155px);
  padding-left: 0;
}

table tr th:last-child,
table tr th:first-child,
table tr td:first-child {
  width: calc(100% - 155px);
}
table tr td:last-child {
  text-align: right;
}

td .material-symbols-outlined {
  margin-left: 5px;
  vertical-align: middle;
}

td.center {
  text-align: center;
}

.icon {
  margin-right: 0.3em;
}

.tag {
  background-color: var(--classic-tag-surface, #eaeaea);
  padding: 0.5em 0.8em;
  border-radius: 2em;
  margin-left: 0.3em;
  display: inline-block;
  font-size: 0.9em;
  line-height: initial;
}

.title_container {
  display: flex;
  margin-bottom: 0;
  align-items: center;
}

.title_container h3 {
  margin-bottom: 0;
}

.title_container .col {
  display: flex;
}

.title_container .left {
  flex: 1;
  display: flex;
}

.title_container .material-symbols-outlined {
  font-size: 1rem;
}

.control {
  font-size: 0.9rem;
}

.close_button {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
  line-height: 1;
  padding: 0;
}

.icon.right {
  margin-left: auto;
}

.big_text {
  font-size: 5rem;
}
</style>
